import {HttpErrorResponse} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {UntypedFormArray} from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {ValidationError as PydanticError} from '@backend';
import {FormlyConfig, FormlyFieldConfig} from '@ngx-formly/core';
import {Observable, of} from 'rxjs';
import {FilePublicationValidationDialogComponent} from './file-publication-validation-dialog/file-publication-validation-dialog.component';
import {ValidationErrorSnackbarComponent} from './validationerrorsnackbar.component';

type Problem = {field: string; message: Observable<string>};
type Problems = Problem[];

function buildFieldMap(
  fieldMap: {[fieldKey: string]: FormlyFieldConfig},
  field: FormlyFieldConfig,
  prefix?: (string | number)[],
) {
  let key: (string | number)[] = [];
  if (prefix) key = [...prefix];
  if (field.key) {
    if (Array.isArray(field.key)) {
      for (let subkey of field.key) {
        key.push(subkey);
      }
    } else {
      key.push(field.key);
    }

    fieldMap[key.filter(elem => elem).join('.')] = field;
  }
  if (field.fieldGroup) {
    for (let subfield of field.fieldGroup) {
      buildFieldMap(fieldMap, subfield, key);
    }
  }
  //TODO: We don't have ragged arrays; when we do, figure out how to map one form field config to multiple form controls.
  if (field.fieldArray) {
    let subfield = field.fieldArray;
    if (typeof subfield === 'function') {
      subfield = subfield(field);
    }
    let arrayLength = 0;
    if (field.formControl) {
      arrayLength = (<UntypedFormArray>field.formControl).length;
    }
    for (let i = 0; i < arrayLength; i++) {
      buildFieldMap(fieldMap, subfield, [...key, i]);
    }
  }
}

function getFieldName(field: FormlyFieldConfig) {
  let parentName = field.parent ? getFieldName(field.parent) : '';
  let myName = field.props?.label ?? '';
  if (parentName && myName) return `${parentName} > ${myName}`;
  if (parentName) return parentName;
  return myName;
}

function parseDetailString(
  detail: string,
  fieldMap: {[fieldKey: string]: FormlyFieldConfig},
): {field: string; message: string} {
  // Extract the field name from a pattern like "Invalid state abbreviation in {{field_name}} field"
  let field: FormlyFieldConfig = undefined;
  detail = detail.replace(/in\s+(\w+)\s+field/, (match: string, group: string) => {
    for (let f in fieldMap) {
      if (f.includes(group)) {
        field = fieldMap[f];
        return '';
      }
    }
    return match;
  });
  return {
    field: field ? getFieldName(field) : 'Whole Form',
    message: detail,
  };
}

@Injectable({providedIn: 'root'})
export class ValidationErrorHandler {
  constructor(
    private readonly dialogService: MatDialog,
    private readonly snackbarService: MatSnackBar,
    private readonly formlyConfig: FormlyConfig,
  ) {}

  private getProblemMessage(error: string, errorData: any, field: FormlyFieldConfig): Observable<string> {
    // Shamelessly adapted from Formly's validation message control
    // (https://github.com/ngx-formly/ngx-formly/blob/main/src/core/src/lib/templates/formly.validation-message.ts#L48)

    let message: string | Observable<string> | ((data: any, field: FormlyFieldConfig) => string | Observable<string>) =
      this.formlyConfig.getValidatorMessage(error);
    message = errorData?.message ?? message;
    message = field?.validation?.messages?.[error] ?? message;
    message = field?.validators?.[error]?.message ?? message;
    message = field?.asyncValidators?.[error]?.message ?? message;
    if (typeof message === 'function') {
      message = message(errorData, field);
    }
    message ??= "There's a problem with this field.";
    if (typeof message === 'string') message = of(message);
    return message;
  }

  private addFormlyProblems(field: FormlyFieldConfig, problems: Problems) {
    if (field.formControl?.errors) {
      for (const error in field.formControl.errors) {
        // Skip generic 'invalid' status.
        if (error === 'invalid') continue;

        problems.push({
          field: getFieldName(field),
          message: this.getProblemMessage(error, field.formControl.errors[error], field),
        });
      }
    }

    if (field.fieldGroup) {
      for (let subfield of field.fieldGroup) {
        this.addFormlyProblems(subfield, problems);
      }
    }
  }

  public buildProblems(form: FormlyFieldConfig[], httpError?: HttpErrorResponse) {
    const fieldMap: {[fieldName: string]: FormlyFieldConfig} = {};
    for (let field of form) {
      buildFieldMap(fieldMap, field);
    }
    const problems: Problems = [];

    if (httpError) {
      switch (httpError.status) {
        case 0:
          problems.push({field: 'Connection', message: of(httpError.message)});
          return problems;
        //@ts-expect-error intentional fallthrough
        case 422: //Pydantic, usually
          if (httpError.error['detail']) {
            const detail = httpError.error['detail'];
            if (detail) {
              // Handle both array of errors (Pydantic validation) and simple string message
              if (typeof detail === 'string') {
                const fieldAndMessage = parseDetailString(detail, fieldMap);
                problems.push({
                  field: fieldAndMessage.field,
                  message: of(fieldAndMessage.message),
                });
              } else if (Array.isArray(detail)) {
                const errors: PydanticError[] = detail;
                for (let error of errors) {
                  const lookupKey = error.loc.join('.');
                  let field =
                    fieldMap[lookupKey] ??
                    fieldMap[lookupKey.replace(/^body\./, '')] ??
                    fieldMap[lookupKey.replace(/^body\.\w+\./, '')];
                  let fieldName = field ? getFieldName(field) : lookupKey;
                  problems.push({
                    field: fieldName,
                    message: this.getProblemMessage(
                      error.type,
                      {...error.ctx, input: error.input, message: error.msg},
                      field,
                    ),
                  });
                }
              }
            }
            return problems;
            //else fallthrough
          }
        default:
          if (httpError.error['errors']) {
            // Assumed to be ErrorResponsesModel
            for (let error of httpError.error['errors'] as {code: string; message: string}[]) {
              problems.push({field: 'Whole Form', message: of(error.message)});
            }
          } else if (httpError.error['code']) {
            // assumed to be ErrorResponseModel)
            problems.push({field: 'Whole Form', message: of(httpError.error['message'])});
          } else if (httpError.error['detail']) {
            // assumed to be { detail: string }
            problems.push({field: 'Whole Form', message: of(httpError.error['detail'])});
          } else {
            // assumed to be a barestring
            problems.push({field: 'Whole Form', message: of(httpError.error.toString())});
          }
      }
    } else {
      for (let field of form) {
        this.addFormlyProblems(field, problems);
      }
    }
    return problems;
  }

  public handleError(form: FormlyFieldConfig[], httpError?: HttpErrorResponse, isMakingDatasetFilePublic?: boolean) {
    if (httpError?.status === 403 && Array.isArray(httpError.error['detail']?.['options'])) {
      this.dialogService.open(FilePublicationValidationDialogComponent, {
        data: {isSpeculation: false, isDirect: !!isMakingDatasetFilePublic, error: httpError},
      });
    } else {
      this.snackbarService.openFromComponent(ValidationErrorSnackbarComponent, {
        data: {problems: this.buildProblems(form, httpError)},
        duration: 15000,
        panelClass: 'snackbar-error',
      });
    }
  }
}
